/**
 * Query Key Factory
 * Centralized query key definitions for React Query
 *
 * Query keys follow a hierarchical pattern:
 * - Base key: ['entity']
 * - List: ['entity', 'list']
 * - Detail: ['entity', 'detail', id]
 * - Filtered: ['entity', 'list', { filter: 'value' }]
 *
 * This pattern ensures:
 * - Easy invalidation (invalidate all projects with ['projects'])
 * - Specific invalidation (invalidate single project with ['projects', 'detail', id])
 * - Predictable caching behavior
 */

export const queryKeys = {
  // Projects
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.projects.lists(), filters] as const,
    user: (email: string) => [...queryKeys.projects.all, 'user', email] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
  },

  // Installations
  installations: {
    all: ['installations'] as const,
    lists: () => [...queryKeys.installations.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.installations.lists(), filters] as const,
    byIds: (ids: string[]) => [...queryKeys.installations.all, 'byIds', ids.sort()] as const, // Sort for consistent caching
    details: () => [...queryKeys.installations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.installations.details(), id] as const,
  },

  // Measurements
  measurements: {
    all: ['measurements'] as const,
    history: (installationId: string, limit: number) =>
      [...queryKeys.measurements.all, 'history', installationId, limit] as const,
    snapshot: (installationId: string, at: string | null) =>
      [...queryKeys.measurements.all, 'snapshot', installationId, at] as const,
    analytics: (installationId: string, combo: string | null) =>
      [...queryKeys.measurements.all, 'analytics', installationId, combo] as const,
    latestTimes: () => [...queryKeys.measurements.all, 'latest-times'] as const,
    cabinetHistory: (installationId: string, limit: number) =>
      [...queryKeys.measurements.all, 'cabinet-history', installationId, limit] as const,
    stationHealth: (installationId: string) =>
      [...queryKeys.measurements.all, 'station-health', installationId] as const,
  },

  // Polygons
  polygons: {
    all: ['polygons'] as const,
    byInstallation: (installationId: string) =>
      [...queryKeys.polygons.all, 'installation', installationId] as const,
  },

  // Alerts
  alerts: {
    all: ['alerts'] as const,
    lists: () => [...queryKeys.alerts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.alerts.lists(), filters ?? {}] as const,
    summary: () => [...queryKeys.alerts.all, 'summary'] as const,
  },

  // Site Images (v1 — kept for backward compat)
  siteImages: {
    all: ['siteImages'] as const,
    lists: () => [...queryKeys.siteImages.all, 'list'] as const,
    byName: (name: string) => [...queryKeys.siteImages.all, 'name', name] as const,
  },

  // Files (v2 — DB-tracked)
  files: {
    all: ['files'] as const,
    byInstallation: (id: string) => [...queryKeys.files.all, 'installation', id] as const,
    byProject: (id: string) => [...queryKeys.files.all, 'project', id] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.users.lists(), filters] as const,
    byProject: (projectId: string) => [...queryKeys.users.all, 'project', projectId] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // Companies
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
  },

  // Documents
  documents: {
    all: ['documents'] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    byName: (name: string) => [...queryKeys.documents.all, 'name', name] as const,
  },
} as const;
